import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PropertyCard from "../components/PropertyCard";
import {
  Search,
  MapPin,
  ShieldCheck,
  Building2,
  CalendarCheck,
  Video,
  Calculator,
  TrendingUp,
  Users,
  Star,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Home,
  Key,
  Rocket,
  Clock,
  HelpCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({
    city: "",
    property_type: "",
    category: "",
    budget: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, citiesRes] = await Promise.all([
          axios.get(`${API}/properties/featured?limit=6`),
          axios.get(`${API}/cities`),
        ]);
        setFeaturedProperties(featuredRes.data);
        setCities(citiesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchFilters.city) params.append("city", searchFilters.city);
    if (searchFilters.property_type) params.append("property_type", searchFilters.property_type);
    if (searchFilters.category) params.append("category", searchFilters.category);
    if (searchFilters.budget) {
      const [min, max] = searchFilters.budget.split("-");
      if (min) params.append("min_price", min);
      if (max) params.append("max_price", max);
    }
    navigate(`/search?${params.toString()}`);
  };

  const budgetOptions = [
    { value: "0-5000000", label: "Under ₹50L" },
    { value: "5000000-10000000", label: "₹50L - ₹1Cr" },
    { value: "10000000-20000000", label: "₹1Cr - ₹2Cr" },
    { value: "20000000-50000000", label: "₹2Cr - ₹5Cr" },
    { value: "50000000-", label: "Above ₹5Cr" },
  ];

  const propertyTypes = [
    { value: "Apartment", label: "Apartment" },
    { value: "Villa", label: "Villas" },
    { value: "Plot", label: "Plots" },
  ];

  const propertyStatuses = [
    { value: "ready_to_move", label: "Ready to Move" },
    { value: "under_construction", label: "Under Construction" },
    { value: "new_launch", label: "New Launch" },
    { value: "upcoming", label: "Upcoming Projects" },
  ];

  const categories = [
    { 
      value: "ready_to_move", 
      label: "Ready to Move", 
      icon: Key,
      image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600",
      description: "Move in immediately - Completed projects with OC"
    },
    { 
      value: "under_construction", 
      label: "Under Construction", 
      icon: Building2,
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600",
      description: "Book now, pay on possession - Best prices"
    },
    { 
      value: "new_launch", 
      label: "New Launch", 
      icon: Rocket,
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600",
      description: "Fresh inventory with early-bird pricing"
    },
    { 
      value: "upcoming", 
      label: "Upcoming Projects", 
      icon: Clock,
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600",
      description: "Pre-launch opportunities - Register interest"
    },
  ];

  const trustPoints = [
    {
      icon: ShieldCheck,
      title: "100% RERA Verified",
      description: "Every project is RERA registered & verified directly from state authority",
    },
    {
      icon: Users,
      title: "Zero Brokerage",
      description: "No hidden charges. Save lakhs on your home purchase",
    },
    {
      icon: Video,
      title: "Virtual Site Visits",
      description: "HD video tours from the comfort of your home",
    },
    {
      icon: Calculator,
      title: "Financial Tools",
      description: "EMI calculator, loan eligibility & complete cost breakdown",
    },
  ];

  const stats = [
    { value: "500+", label: "RERA Projects" },
    { value: "6", label: "Metro Cities" },
    { value: "50K+", label: "Happy Families" },
    { value: "₹0", label: "Brokerage" },
  ];

  const faqs = [
    {
      question: "What is RERA and why is it important?",
      answer: "RERA (Real Estate Regulatory Authority) is a regulatory body established under the Real Estate (Regulation and Development) Act, 2016. It protects homebuyers by ensuring transparency, accountability, and timely delivery of projects. All projects on our platform are RERA registered, giving you legal protection and peace of mind."
    },
    {
      question: "Is there any brokerage or hidden charges?",
      answer: "No, we operate on a zero brokerage model for homebuyers. You don't pay any commission or hidden fees. Our revenue comes from developers and builders, so you get the best deal directly."
    },
    {
      question: "How do I schedule a site visit?",
      answer: "You can schedule a site visit by clicking the 'Schedule Site Visit' button on any property page. Fill in your details and preferred date/time, and our relationship manager will confirm your appointment within 24 hours."
    },
    {
      question: "Can I get a virtual tour of properties?",
      answer: "Yes! We offer HD video tours and virtual site visits for most properties. Click on 'Book Video Tour' on the property page to schedule a live virtual walkthrough with our property experts."
    },
    {
      question: "How do I verify if a project is RERA registered?",
      answer: "Each property listing on our platform displays the RERA registration number. You can click on the 'Verify on RERA' link to check the registration directly on the respective state RERA portal."
    },
    {
      question: "Do you help with home loans?",
      answer: "Yes, we provide home loan assistance including EMI calculation, loan eligibility check, and connections with leading banks. Use our EMI Calculator to plan your finances and our team can help you get the best loan rates."
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[100vh] flex items-end pb-16 md:pb-24" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1716396674809-f9e9ef01d10e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxtdW1iYWklMjBza3lsaW5lJTIwc3Vuc2V0JTIwc2VhJTIwbGlua3xlbnwwfHx8fDE3NzE4NTcyODJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Mumbai Skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60" />
        </div>

        <div className="section-container relative z-10 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* RERA Stamp */}
            <div className="inline-flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 mb-6 animate-fade-in">
              <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium uppercase tracking-wider">100% RERA Verified Properties</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-white leading-[0.95] mb-6 animate-fade-in stagger-1">
              Find Your Dream Home,{" "}
              <span className="text-amber-400">RERA Approved</span>
            </h1>

            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-8 animate-fade-in stagger-2">
              India's most trusted platform for verified residential properties across 6 metro cities. Zero brokerage. Complete transparency.
            </p>
          </div>

          {/* Search Panel - Full Width */}
          <div className="w-full glass p-6 animate-fade-in stagger-3" data-testid="search-panel">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* City Select */}
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">City</label>
                <Select
                  value={searchFilters.city}
                  onValueChange={(value) => setSearchFilters({ ...searchFilters, city: value })}
                >
                  <SelectTrigger className="h-12 border-0 border-b-2 border-stone-200 rounded-none focus:border-emerald-900" data-testid="city-select">
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.slug} value={city.name}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Type Select */}
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Property Type</label>
                <Select
                  value={searchFilters.property_type}
                  onValueChange={(value) => setSearchFilters({ ...searchFilters, property_type: value })}
                >
                  <SelectTrigger className="h-12 border-0 border-b-2 border-stone-200 rounded-none focus:border-emerald-900" data-testid="property-type-select">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Status Select */}
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Property Status</label>
                <Select
                  value={searchFilters.category}
                  onValueChange={(value) => setSearchFilters({ ...searchFilters, category: value })}
                >
                  <SelectTrigger className="h-12 border-0 border-b-2 border-stone-200 rounded-none focus:border-emerald-900" data-testid="category-select">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Budget Select */}
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Budget</label>
                  <Select
                    value={searchFilters.budget}
                    onValueChange={(value) => setSearchFilters({ ...searchFilters, budget: value })}
                  >
                    <SelectTrigger className="h-12 border-0 border-b-2 border-stone-200 rounded-none focus:border-emerald-900" data-testid="budget-select">
                      <SelectValue placeholder="Any Budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <Button
                    onClick={handleSearch}
                    className="w-full h-12 btn-primary flex items-center justify-center gap-2"
                    data-testid="hero-search-btn"
                  >
                    <Search className="w-4 h-4" strokeWidth={1.5} />
                    Search
                  </Button>
                </div>
              </div>
            </div>

          {/* Quick Stats - Centered */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 mt-8 animate-fade-in stagger-4">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-mono text-2xl md:text-3xl text-white">{stat.value}</p>
                <p className="text-white/60 text-xs uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" strokeWidth={1} />
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-spacing bg-stone-50" data-testid="categories-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-amber-600 font-medium mb-3">Browse by Category</p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900">What Are You Looking For?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, i) => (
              <Link
                key={cat.value}
                to={`/search?category=${cat.value}`}
                className="property-card overflow-hidden hover-lift group"
                data-testid={`category-${cat.value}`}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={cat.image} 
                    alt={cat.label}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-sm flex items-center justify-center mb-3">
                      <cat.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-xl text-white mb-1">{cat.label}</h3>
                    <p className="text-white/70 text-sm">{cat.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section-spacing" data-testid="featured-section">
        <div className="section-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-600 font-medium mb-3">Handpicked for You</p>
              <h2 className="font-display text-3xl md:text-4xl text-stone-900">Featured Projects</h2>
            </div>
            <Link
              to="/search?featured=true"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-featured"
            >
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-stone-100 animate-pulse aspect-[4/3] rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProperties.slice(0, 1).map((prop) => (
                <PropertyCard key={prop.property_id} property={prop} variant="featured" />
              ))}
              {featuredProperties.slice(1, 5).map((prop) => (
                <PropertyCard key={prop.property_id} property={prop} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why RERA Section */}
      <section className="section-spacing bg-emerald-900 relative overflow-hidden" data-testid="why-rera-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-medium mb-3">Our Promise</p>
            <h2 className="font-display text-3xl md:text-5xl text-white mb-4">Why Only RERA-Verified Projects?</h2>
            <p className="text-emerald-100/70 max-w-2xl mx-auto">
              The Real Estate Regulatory Authority (RERA) ensures transparency, accountability, and buyer protection in every transaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustPoints.map((point, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-colors"
              >
                <div className="w-12 h-12 bg-amber-500 rounded-sm flex items-center justify-center mb-4">
                  <point.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl text-white mb-2">{point.title}</h3>
                <p className="text-emerald-100/60 text-sm">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Section */}
      <section className="section-spacing" data-testid="cities-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-amber-600 font-medium mb-3">Explore</p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900">Properties Across 6 Metro Cities</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {cities.map((city, i) => (
              <Link
                key={city.slug}
                to={`/city/${city.slug}`}
                className={`relative overflow-hidden group ${
                  i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-[4/3]" : "aspect-[4/3]"
                }`}
                data-testid={`city-${city.slug}`}
              >
                <img
                  src={city.image}
                  alt={city.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <h3 className={`font-display text-white ${i === 0 ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
                    {city.name}
                  </h3>
                  <p className="text-white/70 text-sm">
                    {city.property_count || 0} Properties
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Features */}
      <section className="section-spacing bg-stone-50" data-testid="tools-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-600 font-medium mb-3">Financial Tools</p>
              <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-6">
                Plan Your Home Purchase with Confidence
              </h2>
              <p className="text-stone-600 mb-8">
                Use our comprehensive financial tools to understand your budget, calculate EMIs, check loan eligibility, and get a complete cost breakdown before making your decision.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-900">EMI Calculator</h4>
                    <p className="text-sm text-stone-500">Calculate your monthly payments with different loan amounts & tenures</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-900">Price Trends</h4>
                    <p className="text-sm text-stone-500">Historical price data & demand indicators for informed decisions</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Home className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-900">Loan Eligibility</h4>
                    <p className="text-sm text-stone-500">Check your home loan eligibility with top banks</p>
                  </div>
                </div>
              </div>

              <Link to="/emi-calculator" className="btn-primary inline-flex items-center gap-2 mt-8" data-testid="tools-cta">
                Try EMI Calculator <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7642037/pexels-photo-7642037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Happy Family"
                className="w-full rounded-sm shadow-float"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-layered">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" strokeWidth={1.5} fill="white" />
                  </div>
                  <div>
                    <p className="font-mono text-2xl text-stone-900">4.8/5</p>
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Customer Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-spacing" data-testid="testimonial-section">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-6 h-6 text-amber-500" strokeWidth={1.5} fill="currentColor" />
              ))}
            </div>
            <blockquote className="font-display text-2xl md:text-4xl text-stone-900 mb-8 leading-relaxed">
              "RERA Approved made our home buying journey completely stress-free. The transparency and zero brokerage saved us over ₹5 lakhs!"
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <img
                src="https://images.unsplash.com/photo-1761891930310-ecee801d652f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBmYW1pbHklMjBoYXBweSUyMG5ldyUyMGhvbWUlMjBrZXlzfGVufDB8fHx8MTc3MTg1NzI4NHww&ixlib=rb-4.1.0&q=85"
                alt="Customer"
                className="w-14 h-14 rounded-full object-cover"
              />
              <div className="text-left">
                <p className="font-medium text-stone-900">Rajesh & Priya Sharma</p>
                <p className="text-sm text-stone-500">Homeowners, Bengaluru</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing bg-stone-50" data-testid="faq-section">
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-widest text-amber-600 font-medium mb-3">Got Questions?</p>
              <h2 className="font-display text-3xl md:text-4xl text-stone-900">Frequently Asked Questions</h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem 
                  key={i} 
                  value={`faq-${i}`}
                  className="bg-white border border-stone-100 rounded-sm px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left font-medium text-stone-900 hover:no-underline py-5">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span>{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 pb-5 pl-8">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-8">
              <p className="text-stone-500 mb-4">Still have questions?</p>
              <Link to="/search" className="btn-secondary inline-flex items-center gap-2">
                Contact Us <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
